/**
 * 意见反馈 - API 工厂函数
 *
 * @description 使用依赖注入模式，支持 Web/App 多端复用
 * @module api/feedback/factory
 * @author AI Assistant
 * @version 2.0.0
 * @since 2026-01-21
 *
 * 对应后端文件：
 * - Controller: backend/app/Modules/Feedback/Controllers/FeedbackController.php
 * - Routes: backend/app/Modules/Feedback/Routes/api.php
 */

import type { IRequestClient } from '../core/client'
import type {
  AddCommentForm,
  CreateFeedbackForm,
  Feedback,
  FeedbackComment,
  FeedbackOption,
  FeedbackQueryParams,
  FeedbackStatistics,
  MyFeedbackQueryParams,
} from '../../types/feedback'

import { FEEDBACK_ROUTES } from './routes'

/**
 * 分页响应格式（后端 paginate() 方法返回）
 */
export interface FeedbackPaginatedResponse {
  items: Feedback[]
  pagination: {
    total: number
    perPage: number
    currentPage: number
    lastPage: number
  }
}

/**
 * 意见反馈 API 接口定义
 */
export interface FeedbackApi {
  // ==================== 列表查询 ====================

  /**
   * 获取反馈列表（Web 端管理）
   *
   * @permission system:feedback:view
   */
  getList: (params?: FeedbackQueryParams) => Promise<FeedbackPaginatedResponse>

  /**
   * 获取我的反馈列表（App 端用户）
   *
   * @permission 登录即可
   */
  getMyFeedbacks: (params?: MyFeedbackQueryParams) => Promise<FeedbackPaginatedResponse>

  // ==================== 详情查询 ====================

  /**
   * 获取我的反馈详情（App 端用户）
   * 包含评论列表
   *
   * @permission 登录即可，只能查看自己的反馈
   */
  getMyDetail: (id: number) => Promise<Feedback>

  /**
   * 获取反馈详情（Web 端管理员）
   * 包含评论列表
   *
   * @permission system:feedback:view
   */
  getDetail: (id: number) => Promise<Feedback>

  /**
   * 获取反馈评论列表
   *
   * @permission system:feedback:view
   */
  getComments: (id: number) => Promise<FeedbackComment[]>

  /**
   * 获取统计信息
   *
   * @permission system:feedback:view
   */
  getStatistics: () => Promise<FeedbackStatistics>

  // ==================== 创建操作 ====================

  /**
   * 创建反馈（App 端用户）
   *
   * @permission 登录即可
   */
  create: (data: CreateFeedbackForm) => Promise<Feedback>

  // ==================== 评论操作 ====================

  /**
   * 用户追问（App 端用户添加评论）
   *
   * @permission 登录即可，只能对自己的反馈追问
   */
  addUserComment: (id: number, data: AddCommentForm) => Promise<FeedbackComment>

  /**
   * 管理员回复（Web 端管理员添加评论）
   *
   * @permission system:feedback:reply
   */
  reply: (id: number, reply: string, images?: string[]) => Promise<Feedback>

  // ==================== 状态操作 ====================

  /**
   * 用户确认解决（App 端用户）
   *
   * @permission 登录即可，只能确认自己的反馈
   */
  confirmResolved: (id: number) => Promise<Feedback>

  /**
   * 更新反馈状态（Web 端管理员）
   *
   * @permission system:feedback:update-status
   */
  updateStatus: (id: number, status: string) => Promise<Feedback>

  // ==================== 删除操作 ====================

  /**
   * 删除反馈
   *
   * @permission system:feedback:delete
   */
  delete: (id: number) => Promise<void>

  // ==================== 选项列表 ====================

  /**
   * 获取反馈类型列表
   */
  getTypes: () => Promise<FeedbackOption[]>

  /**
   * 获取反馈状态列表
   */
  getStatuses: () => Promise<FeedbackOption[]>
}

/**
 * 创建意见反馈 API 实例
 *
 * @param client 请求客户端（支持 axios、alova 等）
 * @returns FeedbackApi 实例
 *
 * @example
 * ```typescript
 * // Web 端 (axios)
 * import { requestClient } from '#/api/request'
 * const feedbackApi = createFeedbackApi(requestClient)
 *
 * // App 端 (alova)
 * import { alovaAdapter } from '@/utils/bayesMesHttp'
 * const feedbackApi = createFeedbackApi(alovaAdapter)
 * ```
 */
export function createFeedbackApi(client: IRequestClient): FeedbackApi {
  return {
    // ==================== 列表查询 ====================

    getList: (params) =>
      client.get<FeedbackPaginatedResponse>(FEEDBACK_ROUTES.list, { params }),

    getMyFeedbacks: (params) =>
      client.get<FeedbackPaginatedResponse>(FEEDBACK_ROUTES.my, { params }),

    // ==================== 详情查询 ====================

    getMyDetail: (id) =>
      client.get<Feedback>(FEEDBACK_ROUTES.myDetail(id)),

    getDetail: (id) =>
      client.get<Feedback>(FEEDBACK_ROUTES.detail(id)),

    getComments: (id) =>
      client.get<FeedbackComment[]>(FEEDBACK_ROUTES.comments(id)),

    getStatistics: () =>
      client.get<FeedbackStatistics>(FEEDBACK_ROUTES.statistics),

    // ==================== 创建操作 ====================

    create: (data) =>
      client.post<Feedback>(FEEDBACK_ROUTES.create, data),

    // ==================== 评论操作 ====================

    addUserComment: (id, data) =>
      client.post<FeedbackComment>(FEEDBACK_ROUTES.myComment(id), data),

    reply: (id, reply, images) =>
      client.post<Feedback>(FEEDBACK_ROUTES.reply(id), { reply, images }),

    // ==================== 状态操作 ====================

    confirmResolved: (id) =>
      client.post<Feedback>(FEEDBACK_ROUTES.myResolve(id), {}),

    updateStatus: (id, status) =>
      client.put<Feedback>(FEEDBACK_ROUTES.updateStatus(id), { status }),

    // ==================== 删除操作 ====================

    delete: (id) =>
      client.delete<void>(FEEDBACK_ROUTES.delete(id)),

    // ==================== 选项列表 ====================

    getTypes: () =>
      client.get<FeedbackOption[]>(FEEDBACK_ROUTES.types),

    getStatuses: () =>
      client.get<FeedbackOption[]>(FEEDBACK_ROUTES.statuses),
  }
}
